<template>
    <div :class="[styles.CONTAINER.FLUID, 'form-padding', 'vue-form-renderer']">

        <!-- Render Form Tag Or Not -->
        <form
            v-if="formData.formConfig.renderFormTag"
            :action="formData.formConfig.formActionURL"
            :method="formData.formConfig.formMethod"
            :id="formTagId"
            @submit.prevent
        >

            <!-- The practice logo is NOT rendered here. Every host (ViewForm, ViewSubmission,
                 ViewSubmissionForPDF) renders it in its own size-capped header; the legacy
                 uncapped block that lived here was hidden by a ViewForm override and never
                 received `logoUrl` from the other hosts. Removed 2026-08-27 — see
                 cypress/node-tests/logo-size-cap-guardrail.test.js. -->

            <!-- form headline -->
            <div class="form-headline-container" v-show="formData.formConfig.isShowHeadline">
                <h1 v-text="localizedHeadline"></h1>
                <p v-text="localizedSubHeadline"></p>
            </div>

            <!-- sections of the form. With break points only the CURRENT part's sections mount:
                 v-if, never v-show (a signature pad mounted at display:none sizes its canvas 0x0).
                 isSectionInPart() is always true unless paging is on, and there is no wrapper
                 element, so the staff view / PDF DOM is unchanged. -->
            <template v-for="(sectionData, index) in sortedSections">
                <SectionContainer
                    v-if="isSectionInPart(sectionData)"
                    :index="index"
                    :section="sectionData"
                    :rows="formData.rows"
                    :controls="formData.controls"
                    :key="sectionData.uniqueId"
                    :value-container="valueContainer"
                    :visible-map="visibleMap"
                    :validation-errors="validationErrors"
                    :read-only="readOnly"
                    :viewSubmissionMode="viewSubmissionMode"
                    :siteID="siteID"
                    :patNum=patNum
                    :logoUrl="logoUrl"
                    :formSubmissionIDForWeb="formSubmissionIDForWeb"
                    :language="language"
                />
            </template>

            <!-- Shown mid-form even when showSubmit is false: the builder preview has no submit
                 but still walks the parts with Continue / Back. -->
            <div v-if="formConfiguration.showSubmit || isPartContinue" class="submit-button-container">
                <!-- "Part 2 of 6" — break points inside this ONE form. Sits above the packet
                     caption (the two can stack); also a sibling of the button, never inside it. -->
                <div v-if="hasParts" class="form-part-caption" data-test="form-part-caption">{{ partCaption }}</div>
                <!-- "Section 3 of 8" (final: "… · last one"). A SIBLING of the button, never inside
                     it — ViewForm's ::v-deep overrides make the button full-width at <=576px. -->
                <div v-if="packetCaption" class="packet-section-caption" data-test="packet-section-caption">{{ packetCaption }}</div>
                <button type="button" class="btn btn-success md-button md-raised md-success md-theme-default submit-button" data-test="form-submit-button" value="Submit" @click="handleSubmit" :disabled="isSubmitting">
                    <span v-if="!isSubmitting">{{ localizedSubmitText }}</span>
                    <span v-else class="submit-button-loading">
                        <i class="fa fa-spinner fa-spin submit-spinner"></i>
                        {{ localizedSubmittingText }}
                    </span>
                </button>
                <button type="button" v-if="showPartBack" class="form-part-back" data-test="form-part-back" @click="goToPart(currentPart - 1)">{{ partBackText }}</button>
            </div>
        </form>
        <template v-else>

            <!-- No logo here either — see the note in the <form> branch above. -->

            <!-- form headline -->
            <div class="form-headline-container" v-show="formData.formConfig.isShowHeadline">
                <h1 v-text="localizedHeadline"></h1>
                <p v-text="localizedSubHeadline"></p>
            </div>

            <!-- sections of the form. With break points only the CURRENT part's sections mount:
                 v-if, never v-show (a signature pad mounted at display:none sizes its canvas 0x0).
                 isSectionInPart() is always true unless paging is on, and there is no wrapper
                 element, so the staff view / PDF DOM is unchanged. -->
            <template v-for="(sectionData, index) in sortedSections">
                <SectionContainer
                    v-if="isSectionInPart(sectionData)"
                    :index="index"
                    :section="sectionData"
                    :rows="formData.rows"
                    :controls="formData.controls"
                    :key="sectionData.uniqueId"
                    :value-container="valueContainer"
                    :visible-map="visibleMap"
                    :validation-errors="validationErrors"
                    :read-only="readOnly"
                    :viewSubmissionMode="viewSubmissionMode"
                    :siteID="siteID"
                    :patNum=patNum
                    :logoUrl="logoUrl"
                    :formSubmissionIDForWeb="formSubmissionIDForWeb"
                    :language="language"
                />
            </template>

            <!-- Shown mid-form even when showSubmit is false: the builder preview has no submit
                 but still walks the parts with Continue / Back. -->
            <div v-if="formConfiguration.showSubmit || isPartContinue" class="submit-button-container">
                <!-- "Part 2 of 6" — break points inside this ONE form. Sits above the packet
                     caption (the two can stack); also a sibling of the button, never inside it. -->
                <div v-if="hasParts" class="form-part-caption" data-test="form-part-caption">{{ partCaption }}</div>
                <!-- "Section 3 of 8" (final: "… · last one"). A SIBLING of the button, never inside
                     it — ViewForm's ::v-deep overrides make the button full-width at <=576px. -->
                <div v-if="packetCaption" class="packet-section-caption" data-test="packet-section-caption">{{ packetCaption }}</div>
                <button type="button" class="btn btn-success md-button md-raised md-success md-theme-default submit-button" data-test="form-submit-button" value="Submit" @click="handleSubmit" :disabled="isSubmitting">
                    <span v-if="!isSubmitting">{{ localizedSubmitText }}</span>
                    <span v-else class="submit-button-loading">
                        <i class="fa fa-spinner fa-spin submit-spinner"></i>
                        {{ localizedSubmittingText }}
                    </span>
                </button>
                <button type="button" v-if="showPartBack" class="form-part-back" data-test="form-part-back" @click="goToPart(currentPart - 1)">{{ partBackText }}</button>
            </div>

        </template>

    </div>
</template>

<script>
    import FormRendererBusiness from "../mixins/form-renderer-mixins";
    import SectionContainer from "../views/renderer/SectionContainer";
    import {EVENT_CONSTANTS} from "../configs/events";
    import {GLOBAL_CONFIG} from "../configs/global";
    import { getLocalizedField } from "../utils/languageHelper";
    import { t } from "../utils/systemTranslations";
    import Validation from "../libraries/validation";
    import { buildParts, clampPart, controlIdsInPart, partIndexForControlKey } from "../libraries/form-parts";


    export default {
        name: "FormRenderer",
        components: {SectionContainer},
        mixins: FormRendererBusiness,

        props: {
            /**
             * Packet state for the form being filled — `resolvePacketProgress()`'s output, or null.
             *
             * DELIBERATELY COMPONENT-LOCAL, not in mixins/form-renderer/configuration.js: only
             * ViewForm (the patient-fill screen) passes it. ViewSubmission.vue and
             * ViewSubmissionForPDF.vue mount this same renderer for staff review and for the PDF
             * filed into Open Dental imaging — they must stay byte-identical, and a shared prop
             * would invite someone to wire it up there "for consistency".
             */
            packetProgress: {
                type: Object,
                default: null
            },

            /**
             * Section break points — "Part 2 of 6" inside ONE form (libraries/form-parts.js).
             *
             * DELIBERATELY COMPONENT-LOCAL, for exactly the reason given for packetProgress above:
             * only ViewForm passes these. ViewSubmission.vue / ViewSubmissionForPDF.vue never page,
             * so the staff view and the PDF filed into Open Dental stay byte-identical. Never move
             * them into mixins/form-renderer/*.
             *
             *   pagingEnabled - split the form at sections whose `breakAfter` is true
             *   draftEnabled  - a patient-specific link that saves progress: the mid-form button
             *                   reads "Save & Continue" instead of "Continue"
             *   initialPart   - 0-based part to land on (a resumed draft's saved part)
             */
            pagingEnabled: {
                type: Boolean,
                default: false
            },
            draftEnabled: {
                type: Boolean,
                default: false
            },
            initialPart: {
                type: Number,
                default: 0
            }
        },

        data: () => ({
            formData: null,
            submitButton: null,
            isSubmitting: false,
            // 0-based part on screen (only meaningful while `hasParts`)
            currentPart: 0,
            // A resume target the current parts cannot reach YET (see the `parts` watcher)
            resumePart: null,
        }),

        computed: {
            /**
             * Get localized headline
             */
            localizedHeadline() {
                if (!this.formData || !this.formData.formConfig) return '';
                return getLocalizedField(this.formData.formConfig, 'headline', this.language);
            },

            /**
             * Get localized sub-headline
             */
            localizedSubHeadline() {
                if (!this.formData || !this.formData.formConfig) return '';
                return getLocalizedField(this.formData.formConfig, 'subHeadline', this.language);
            },

            /**
             * True while this form is a NON-FINAL section of a packet — the one case where the
             * button must not say "Submit". Harrisburg Smiles' patient filled section 1 of 8, saw
             * "Submit", and stopped; the office got 1 of 8.
             */
            isPacketContinue() {
                return !!(this.packetProgress && !this.packetProgress.isLast);
            },

            /**
             * The form's parts, or null when paging is off (staff view, PDF, any host that does
             * not opt in). `visibleMap` is re-$set on every value change (mixins/form-renderer/
             * model.js), so the parts follow the patient's answers live: "No" -> 1 part, "Yes" -> 6.
             */
            parts() {
                if (!this.pagingEnabled) return null;
                return buildParts(this.sortedSections, this.visibleMap);
            },

            /** More than one part right now — the only state in which any part UI shows. */
            hasParts() {
                return !!(this.parts && this.parts.length > 1);
            },

            isFinalPart() {
                return !this.hasParts || this.currentPart >= this.parts.length - 1;
            },

            /**
             * A NON-final part: the button validates THIS part only and moves on instead of
             * submitting. The final part's button is exactly today's button.
             */
            isPartContinue() {
                return this.hasParts && !this.isFinalPart;
            },

            /** "Part 2 of 6" */
            partCaption() {
                if (!this.hasParts) return '';
                return t('partOf', this.language, {
                    step: this.currentPart + 1,
                    total: this.parts.length
                });
            },

            showPartBack() {
                return this.hasParts && this.currentPart > 0;
            },

            partBackText() {
                return t('back', this.language);
            },

            /**
             * Get localized submit button text
             */
            localizedSubmitText() {
                // Mid-form part (break points): Continue, or Save & Continue when this link saves
                // progress. Checked BEFORE the packet wording — the packet button belongs to the
                // form's final part only.
                if (this.isPartContinue) return t(this.draftEnabled ? 'saveAndContinue' : 'continue', this.language);

                // Mid-packet the wording is the platform's, not the form's: a form's own
                // submitButtonText ("Submit", or an office's "Next" stopgap) is about the form
                // standing alone. The FINAL section falls through to the normal logic below, so a
                // custom text still wins there.
                if (this.isPacketContinue) return t('saveAndContinue', this.language);

                // First try form-specific custom text
                const formConfig = this.formConfiguration?.formConfig || this.formData?.formConfig;
                if (formConfig) {
                    const customText = getLocalizedField(formConfig, 'submitButtonText', this.language);
                    if (customText) return customText;
                }
                // Fall back to system translation
                return t('submit', this.language);
            },

            /**
             * Get localized submitting text
             */
            localizedSubmittingText() {
                if (this.isPartContinue) return t('saving', this.language);
                if (this.isPacketContinue) return t('saving', this.language);
                return t('submitting', this.language);
            },

            /**
             * "Section 3 of 8", or "Section 8 of 8 · last one" on the final section.
             * Null when the numbers are unavailable (a pre-deploy link, or a corrupt query — see
             * libraries/packet-progress.js): the button override still applies without it.
             */
            packetCaption() {
                // Packet-step wording belongs to the packet button, which only the form's final
                // part shows; mid-form the part caption orients the patient instead.
                if (this.isPartContinue) return null;
                if (!this.packetProgress || !this.packetProgress.total) return null;
                const base = t('sectionOf', this.language, {
                    step: this.packetProgress.step,
                    total: this.packetProgress.total
                });
                return this.packetProgress.isLast
                    ? `${base} · ${t('lastSection', this.language)}`
                    : base;
            }
        },

        watch: {
            /**
             * Parts shrink when an answer hides gated sections: keep the patient on a part that
             * exists. A resume target (initialPart) the parts could not reach yet is honoured as
             * soon as they can: the renderer mounts with EMPTY values and the resumed answers
             * (e.g. the "Yes" that reveals five parts) arrive a tick later via `value`.
             */
            parts(newParts) {
                if (this.resumePart !== null && Array.isArray(newParts)) {
                    if (this.resumePart < newParts.length) {
                        this.currentPart = this.resumePart;
                        this.resumePart = null;
                    } else {
                        // not reachable (yet): stand on the furthest part that exists
                        this.currentPart = clampPart(this.resumePart, newParts);
                    }
                    return;
                }
                const clamped = clampPart(this.currentPart, newParts);
                if (clamped !== this.currentPart) this.currentPart = clamped;
            },

            initialPart: {
                immediate: true,
                handler(val) {
                    const n = Math.floor(Number(val));
                    const requested = Number.isFinite(n) && n > 0 ? n : 0;
                    const clamped = clampPart(requested, this.parts);
                    this.currentPart = clamped;
                    this.resumePart = requested > clamped ? requested : null;
                }
            },

            /**
             * An error on an EARLIER part (final Submit validates every part) jumps the patient
             * there. setValidationError() sets validationErrors synchronously and scrolls in a
             * $nextTick; this watcher and the re-render run in the flush before that scroll, so
             * the field exists when the scroll looks for it.
             */
            validationErrors(errors) {
                if (!this.hasParts || !errors) return;
                const keys = Object.keys(errors);
                if (!keys.length) return;
                const idx = partIndexForControlKey(this.parts, this.formData, keys[0]);
                if (idx >= 0) {
                    this.resumePart = null;
                    if (idx !== this.currentPart) this.currentPart = idx;
                }
            }
        },

        created() {
            this.$formEvent.$on(EVENT_CONSTANTS.RENDERER.VALIDATION_OK, this.continueProcessAfterValidationOk);
            this.$formEvent.$on(EVENT_CONSTANTS.RENDERER.VALIDATION_FAILED, this.processFailedValidation);
            this.$formEvent.$on('submitComplete', this.resetSubmissionState);
        },

        // Pair every $on above with a $off — without this, packet flow
        // stacks stale listeners on the global $formEvent bus, and stale
        // VALIDATION_OK emissions from prior forms can drive the live form
        // to submit despite its own VALIDATION_FAILED.
        beforeDestroy() {
            this.$formEvent.$off(EVENT_CONSTANTS.RENDERER.VALIDATION_OK, this.continueProcessAfterValidationOk);
            this.$formEvent.$off(EVENT_CONSTANTS.RENDERER.VALIDATION_FAILED, this.processFailedValidation);
            this.$formEvent.$off('submitComplete', this.resetSubmissionState);
        },

        mounted() {
            //console.log(`In FormRenderer`);
            //console.dir(this.formData);
        },

        methods: {
            handleSubmit(e) {
                //console.log(e);
                //console.log(e.target)
                this.submitButton = e;
                this.isSubmitting = true;
                e.target.disabled = true;
                this.isValidationRunning = true;

                // Mid-form part: validate THIS part and move on — never the $formEvent bus (the
                // packet flow's stale-listener race), never a submission.
                if (this.isPartContinue) return this.validateCurrentPart(e);

                this.$formEvent.$emit(EVENT_CONSTANTS.RENDERER.RUN_VALIDATION, true);
                //this.$formEvent.$emit("submit", true);
            },

            /**
             * True when the section belongs to the part on screen. Always true without parts, so
             * every non-paging host renders exactly the sections it always did.
             */
            isSectionInPart(section) {
                if (!this.hasParts) return true;
                const part = this.parts[clampPart(this.currentPart, this.parts)];
                return !!(part && section && part.sectionIds.has(section.uniqueId));
            },

            /** Show part `i` (clamped) and bring the top of the form back into view. */
            goToPart(i) {
                this.resumePart = null;
                this.currentPart = clampPart(i, this.parts);
                this.$nextTick(() => this.scrollToRendererTop());
            },

            /** Same −120px offset as the validation-error scroll (mixins/form-renderer/validation.js). */
            scrollToRendererTop() {
                try {
                    const el = this.$el;
                    if (!el || typeof el.getBoundingClientRect !== 'function') return;
                    const rect = el.getBoundingClientRect();
                    const top = window.pageYOffset + rect.top - 120;
                    window.scrollTo({ top: top > 0 ? top : 0, behavior: 'smooth' });
                } catch (err) {
                    // scrolling is cosmetic — never block the patient on it
                }
            },

            /**
             * Continue on a non-final part: validate ONLY the visible controls of this part (the
             * same visibility rule as the final submit, via getControlsForValidation), then move
             * to the next part and tell the host it may save progress.
             */
            validateCurrentPart() {
                const inPart = controlIdsInPart(this.parts, this.formData, this.currentPart);
                const visible = this.getControlsForValidation();
                const controls = {};
                Object.keys(visible).forEach(controlId => {
                    if (inPart.has(controlId)) controls[controlId] = visible[controlId];
                });

                this.$set(this, 'validationErrors', {});
                const result = new Validation(
                    this.valueContainer,
                    controls,
                    (this.$form && this.$form.validationClosures) || {},
                    this.language,
                ).run();

                if (result.errors()) {
                    // toast + scroll; emits VALIDATION_FAILED, whose handler re-enables the button
                    return this.setValidationError(result.errorBuckets);
                }

                this.isValidationRunning = false;
                const next = this.currentPart + 1;
                this.goToPart(next);
                this.$emit('save-draft', { partIndex: next });
                this.resetSubmissionState();
            },

            /**
             * [VALIDATION-OK-EMIT] Continue to process
             */
             continueProcessAfterValidationOk() {
                this.isValidationRunning = false;
                this.$formEvent.$emit("submit", true);
                // Note: We don't reset isSubmitting here because the parent component will handle the submission
            },

            /**
             * [VALIDATION-FAILED-EMIT] Garbage collecting...
             */
             processFailedValidation() {
                this.isValidationRunning = false;
                this.isSubmitting = false;
                if(this.submitButton) {
                    this.submitButton.target.disabled = false;
                }
            },

            // Reset the submission state on completion (success or failure)
            resetSubmissionState() {
                this.isSubmitting = false;
                if (this.submitButton && this.submitButton.target) {
                    this.submitButton.target.disabled = false;
                }
            },
        }
    }
</script>

<style scoped>
.submit-button-container {
   margin-top: 30px;
   text-align: center;
   width: 100%;
}

/* "Section 3 of 8" above the button. Muted on purpose — it orients, it does not compete
   with the button. Sibling of the button so ViewForm's mobile full-width rule still applies. */
.packet-section-caption {
   font-size: 0.9rem;
   color: #64748b;
   margin-bottom: 8px;
   text-align: center;
   line-height: 1.4;
}

/* "Part 2 of 6" (break points inside ONE form) — same muted tone, directly above the packet
   caption when both show. */
.form-part-caption {
   font-size: 0.9rem;
   color: #64748b;
   margin-bottom: 8px;
   text-align: center;
   line-height: 1.4;
}

/* Back to the previous part — link-styled so it never competes with the main button, and on
   its own line under it (ViewForm keeps the main button full-width at <=576px). */
.form-part-back {
   display: block;
   margin: 12px auto 0;
   padding: 6px 12px;
   background: none;
   border: none;
   color: #0f7259;
   font-size: 16px;
   text-decoration: underline;
   cursor: pointer;
}

.form-part-back:hover,
.form-part-back:focus {
   color: #0b5a46;
}

.submit-button {
   font-size: 18px;
   padding: 12px 30px;
   min-width: 200px;
   transition: all 0.3s ease;
   position: relative;
}

.submit-button:hover {
   transform: translateY(-2px);
   box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
}

.submit-button:active {
   transform: translateY(1px);
}

.submit-button-loading {
   display: flex;
   align-items: center;
   justify-content: center;
}

.submit-spinner {
   margin-right: 8px;
}

/* Mobile responsive styles */
@media (max-width: 576px) {
   .submit-button {
       width: 100%;
       font-size: 16px;
       padding: 10px 20px;
   }
}
</style>